import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  AuctionTimerType,
  TimerVariant,
  TradeStateEnumMap
} from '../../types/trade';
import Timer from '../Timer';
import TimerService from '../../../services/TimerService';
import {
  StyledExtendedProgressContainer,
  StyledExtendedText,
  StyledExtendedProgressParent
} from './Styles';
import ReducerConstants from '../../constants/reducers';

const propTypes = {
  variant: PropTypes.oneOf([
    AuctionTimerType.ACTIVE,
    AuctionTimerType.PENDING,
    AuctionTimerType.CLOSING
  ])
};

const defaultProps = {
  variant: AuctionTimerType.ACTIVE
};

const ActiveTimer = ({
  onAuctionEnded,
  activeExpiryTime,
  location,
  tradeId,
  tradeCardAuctionEnded,
  variant,
  isTimerExtended
}) => {
  const stateChange = useSelector(
    state => state?.[ReducerConstants.STATE_CHANGE][tradeId],
    shallowEqual
  );

  const rafId = useRef(null);

  const timerService = TimerService.get();
  const [timeLeft, setTimeLeft] = useState(
    timerService.getFormattedTimeDifference(
      activeExpiryTime,
      variant,
      isTimerExtended
    ).timeLeft
  );
  const [isTimerAlert, setIsTimerAlert] = useState(false);
  const [isWarnState, setIsWarnState] = useState(false);
  const [showExtendedIndicator, setShowExtendedIndicator] = useState(false);
  const [isAuctionEnded, setIsAuctionEnded] = useState(
    tradeCardAuctionEnded || false
  );

  useEffect(() => {
    if (
      stateChange?.newState !== stateChange?.previousState &&
      stateChange?.newState !== undefined &&
      TradeStateEnumMap[variant] !== stateChange?.newState
    ) {
      setIsAuctionEnded(true);
    }
  }, [stateChange?.previousState, stateChange?.newState, variant]);

  /**
   * Sets trade detail info
   */
  useEffect(() => {
    if (!activeExpiryTime) {
      return () => {};
    }

    let mounted = true;
    const timerMessage = msgType => {
      switch (msgType) {
        case AuctionTimerType.PENDING:
          return <FormattedMessage id="vdp.pendingEnded" />;
        case AuctionTimerType.CLOSING:
          return <FormattedMessage id="vdp.closingEnded" />;
        default:
          return <FormattedMessage id="vdp.auctionEnded" />;
      }
    };

    /**
     * Updates the timer state every second
     */
    const updateTimerInfo = () => {
      const timerInfo = timerService.getFormattedTimeDifference(
        activeExpiryTime,
        variant,
        isTimerExtended
      );
      if (!isAuctionEnded) {
        setTimeLeft(timerInfo?.timeLeft);
        if (timerInfo?.alertState) {
          setIsTimerAlert(true);
          setIsWarnState(false);
        }
        if (timerInfo?.warnState) {
          setIsWarnState(true);
          setIsTimerAlert(false);
        }
        setShowExtendedIndicator(timerInfo?.extendedIndicator);
      } else {
        setTimeLeft(timerMessage(variant));
        setIsTimerAlert(false);
        cancelAnimationFrame(rafId?.current);
      }
      if (timerInfo?.warnState) setIsWarnState(true);
      if (mounted) rafId.current = requestAnimationFrame(updateTimerInfo);
    };

    updateTimerInfo();

    return () => {
      cancelAnimationFrame(rafId.current);
      mounted = false;
    };
  }, [
    timerService,
    activeExpiryTime,
    isAuctionEnded,
    variant,
    isTimerExtended,
    showExtendedIndicator
  ]);

  useEffect(() => {
    if (isAuctionEnded) {
      onAuctionEnded(true);
    }
  }, [isAuctionEnded, onAuctionEnded, variant]);

  const getTimerVariant = () => {
    if (isTimerAlert) {
      return TimerVariant.ALERT;
    }
    if (isAuctionEnded) {
      return TimerVariant.BOLD;
    }
    if (isWarnState) {
      return TimerVariant.WARN;
    }
    return null;
  };

  return timeLeft ? (
    <>
      <Timer
        variant={getTimerVariant()}
        location={location}
        className="notranslate"
        isTimerExtended={isTimerExtended}
        showExtendedIndicator={showExtendedIndicator}
      >
        {timeLeft}
      </Timer>
      {showExtendedIndicator && (
        <StyledExtendedProgressParent>
          <StyledExtendedProgressContainer data-testid="extended-bar">
            <StyledExtendedText>
              <FormattedMessage id="vdp.bid.timeExtended" />
            </StyledExtendedText>
          </StyledExtendedProgressContainer>
        </StyledExtendedProgressParent>
      )}
    </>
  ) : null;
};

ActiveTimer.propTypes = propTypes;
ActiveTimer.defaultProps = defaultProps;

export default ActiveTimer;
